<!--
© Copyright 2015-2026 Personalized Privacy Assistant Project, Carnegie Mellon University - All Rights Reserved - US Patent 11,768,949, US Patent 10,956,586, US Patent 12,235,973, and additional USPTO and EPO patents pending.
This code is proprietary and confidential. For any questions about this code, including licensing, please contact Prof. Norman Sadeh (sadeh@cs.cmu.edu).
-->
<template>
  <div>
    <div class="container-fluid">
      <div class="row">
        <div class="col-md-6" v-html="leftPanelInfo"></div>
        <div class="col-md-6" v-html="rightPanelInfo"></div>
      </div>
    </div>
  </div>
</template>

<script>
    export default {
    props: {
      permissions: Object,
      resourceFramework: Object,
      existingData: [Object, Array],
      associatedIrrs: [Object, Array],
    },
    computed: {
      //computed HTML for the left panel info
      leftPanelInfo: function () {  //Basic Info fields
        var html = '<div class="card shadow mb-3">';
        var firstCategory = this.resourceFramework.categories[Object.keys(this.resourceFramework.categories)[0]];
        html += '<h3 class="card-header">' + firstCategory.display_name + '</h3>'; //category heading
        html += '<div class="card-body"><p class="card-text">' + (this.createFieldAndDataHTML(firstCategory) || '') + '</p>';
        html += '</div></div>'

        // QR code: show only if resource is published in at least one registry
        var hasAssociatedIrrs = false;
        if (this.existingData) {
          if (this.existingData.associatedIrrs && this.existingData.associatedIrrs.length) {
            hasAssociatedIrrs = true;
          } else if (this.existingData.resource && this.existingData.resource.associatedIrrs && this.existingData.resource.associatedIrrs.length) {
            hasAssociatedIrrs = true;
          }
        }

        // also consider associatedIrrs passed explicitly from the parent
        if (!hasAssociatedIrrs && this.associatedIrrs && this.associatedIrrs.length) {
          hasAssociatedIrrs = true;
        }

        if (hasAssociatedIrrs) {
          var resourceId = (this.existingData && this.existingData.id) ? this.existingData.id : (this.existingData && this.existingData.resource && this.existingData.resource.id ? this.existingData.resource.id : '');
          if (resourceId) {
            html += '<div class="card shadow mb-3">';
            html += '<h3 class="card-header">QR Code</h3>'; // qr code heading
            html += '<div class="card-body"><img src="' + window.location.origin + '/api/getqrcode/resource/' + resourceId + '" alt="QR Code" onerror="this.style.display=\'none\'" />';
            html += '</div></div>'
          }
        }

        return html;
      },
      //computed HTML for the right panel info
      rightPanelInfo: function () { //Remaining Info fields
        var html = '';
        var categoryKeys = Object.keys(this.resourceFramework.categories);
        for(var i=2; i<categoryKeys.length; i++) {  //start at index 2.  basic info(0) is displayed elsewhere, map data(1) is shown in the map
          var category = this.resourceFramework.categories[categoryKeys[i]];
          var bodyHtml = this.createFieldAndDataHTML(category);
          if (!bodyHtml || !String(bodyHtml).trim()) {
            continue;
          }
          html += '<div class="card shadow mb-3">';
          html += '<h3 class="card-header">' + category.display_name + '</h3>'; //category heading
          html += '<div class="card-body"><p class="card-text">' + bodyHtml + '</p>';
          html += '</div></div>';
        }
        return html;
      }
    },
    methods: {
      escapeHtml: function(value) {
        if (typeof value !== 'string') {
          return value;
        }

        return value
          .replace(/&/g, '&amp;')
          .replace(/</g, '&lt;')
          .replace(/>/g, '&gt;')
          .replace(/"/g, '&quot;')
          .replace(/'/g, '&#039;');
      },
      safeHref: function(value) {
        if (typeof value !== 'string') {
          return '#';
        }

        var trimmed = value.trim();
        if (!trimmed) {
          return '#';
        }

        if (/^(https?:|mailto:)/i.test(trimmed)) {
          return trimmed;
        }

        return encodeURI(trimmed);
      },
      /**
       * Whether a field value should be omitted from the resource detail view (null, missing, blank string, or empty array).
       */
      isEmptyResourceDetailValue: function(val) {
        if (val === null || typeof val === 'undefined') {
          return true;
        }
        if (typeof val === 'string' && val.trim() === '') {
          return true;
        }
        if (Array.isArray(val) && val.length === 0) {
          return true;
        }
        return false;
      },
      /**
       * HTML for a companion "_other" free-text value (checkbox "Other" or radio "Additional comments").
       */
      buildCompanionOtherValueHtml: function(formField) {
        if (!formField.has_other_field) {
          return '';
        }

        var data = this.existingData;
        if (!data) {
          return '';
        }

        var otherKey = formField.name + '_other';
        if (!(otherKey in data)) {
          return '';
        }

        var otherVal = data[otherKey];
        if (this.isEmptyResourceDetailValue(otherVal)) {
          return '';
        }

        var label = (formField.element_type === 'radio') ? 'Additional comments' : 'Other';
        return '<span class="lead">' + this.escapeHtml(label) + ': ' + this.escapeHtml(String(otherVal).trim()) + '</span>';
      },
      /**
       * "Volunteer Contributor: <name>" row for a listing published by a volunteer (owner_is_author = false), shown where an
       * owner's listing shows the Resource Owner; '' for listings published by the owner / an authorized representative.
       */
      volunteerContributorHtml: function() {
        var data = this.existingData;
        if (!data) {
          return '';
        }

        var role = data.owner_is_author;
        var publishedByVolunteer = (role === false || role === 'false' || role === 0 || role === '0');
        if (!publishedByVolunteer || this.isEmptyResourceDetailValue(data.contributor)) {
          return '';
        }

        var roleField = this.resourceFramework.fields.owner_is_author || {};
        var label = roleField.volunteer_display_name || 'Volunteer Contributor';

        return '<span class="lead font-weight-bold">' + this.escapeHtml(label) + ':<br></span>'
          + '<span class="lead">' + this.escapeHtml(String(data.contributor)) + '</span>'
          + '<span class="lead"><br>&nbsp;<br></span>';
      },
      /**
       * HTML for the value portion of a single framework field, or '' if the field should not be shown.
       */
      buildResourceFieldValueHtml: function(formField) {
        var key = formField.name;
        var data = this.existingData;
        if (!data) {
          return '';
        }

        var val = (key in data) ? data[key] : null;
        var mainEmpty = this.isEmptyResourceDetailValue(val);
        var otherHtml = this.buildCompanionOtherValueHtml(formField);

        if (mainEmpty && !otherHtml) {
          return '';
        }

        var html = '';
        var _this = this;

        if (!mainEmpty) {
          switch (formField.element_type) {
            case 'url':
              html += '<span class="lead"><a href="' + _this.safeHref(val) + '" target="_blank" rel="noopener noreferrer">' + _this.escapeHtml(val) + '</a></span>';
              break;
            case 'email':
              html += '<span class="lead"><a href="mailto:' + encodeURIComponent(val) + '" target="_blank" rel="noopener noreferrer">' + _this.escapeHtml(val) + '</a></span>';
              break;
            case 'file':
              return otherHtml;
            default:
              if (Array.isArray(val)) {
                for (var i = 0; i < val.length; i++) {
                  var dataKeyArr = key + '_' + val[i];
                  if (typeof _this.resourceFramework.data[dataKeyArr] !== 'undefined') {
                    html += '<span class="lead">' + _this.escapeHtml(_this.resourceFramework.data[dataKeyArr].display_value) + ',&nbsp;</span>';
                  } else {
                    html += '<span class="lead">' + _this.escapeHtml(val[i]) + ',&nbsp;</span>';
                  }
                }
                if (html.endsWith(',&nbsp;</span>')) {
                  html = html.substring(0, html.length - 14);
                  html += '</span>';
                }
              } else {
                var dataKeySingle = key + '_' + val;
                if (typeof _this.resourceFramework.data[dataKeySingle] !== 'undefined') {
                  html += '<span class="lead">' + _this.escapeHtml(_this.resourceFramework.data[dataKeySingle].display_value) + '</span>';
                } else {
                  var scalar = val;
                  if (scalar == '1') {
                    scalar = 'Yes';
                  } else if (scalar == '0') {
                    scalar = 'No';
                  }
                  html += '<span class="lead">' + _this.escapeHtml(scalar) + '</span>';
                }
              }
          }
        }

        if (!otherHtml) {
          return html;
        }

        if (!html) {
          return otherHtml;
        }

        if (Array.isArray(val)) {
          return html + '<span class="lead">,&nbsp;</span>' + otherHtml;
        }

        return html + '<span class="lead"><br></span>' + otherHtml;
      },
      createFieldAndDataHTML: function(formCategory) {
        var html = '';

        var _this = this;
        $.each(this.resourceFramework.fields, function(i, formField) { //field names

          if (formField.exclude_from_resource_detail) {
            return true;
          }

          //skip fields not relevent to resource or template depending on which this is
          if(_this.existingData.is_template) {
            if(!formField.template_field) { return true; }
            else {
              formField.display_name = formField.template_display_name;
            }
          }
          else if(formField.template_only_field) { return true; }


          if(formField.form_category_name == formCategory.name && formField.element_type != 'file') {  //if the field belongs under the current heading, and is not a 'file' type
            // a listing published by a volunteer contributor names the contributor where an owner's listing names the Resource Owner
            if (formField.name === 'owner_name') {
              html += _this.volunteerContributorHtml();
            }

            var valueHtml = _this.buildResourceFieldValueHtml(formField);
            if (!valueHtml) {
              return true;
            }

            html += '<span class="lead font-weight-bold">' + _this.escapeHtml(formField.display_name) + ':<br></span>'; //field name
            html += valueHtml;
            html += '<span class="lead"><br>&nbsp;<br></span>'; //space between fields
          }
        });
        return html;
      },
    },
    mounted(){
        //
      },
    }
</script>
